import {
  Calendar,
  ExternalLink,
  Hash,
  Layers,
  RefreshCw,
  Shield,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { DefinitionsMetadata, Metadata } from "data/models";
import dayjs from "dayjs";
import { useIntercom } from "react-use-intercom";
import { Link as RouterLink } from "react-router-dom";
import { formatTimestamp } from "utils/format-date";

interface CatalogueEntryMetadataCardProps {
  metadata: Metadata;
  definitionsMetadata: DefinitionsMetadata;
  latestReleaseReportLink: string;
  productDescriptionLink: string;
}

export const CatalogueEntryMetadataCard = ({
  metadata,
  definitionsMetadata,
  latestReleaseReportLink,
  productDescriptionLink,
}: CatalogueEntryMetadataCardProps) => {
  const { show } = useIntercom();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Metadata</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-2">
            <Calendar className="size-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Date Added</div>
              <div className="text-muted-foreground">
                {formatTimestamp(metadata.dateAdded)}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Calendar className="size-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Last Updated</div>
              <div className="text-muted-foreground">
                {dayjs(definitionsMetadata.latestRelease).format("MMM YYYY")}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Layers className="size-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Data Type</div>
              <div className="text-muted-foreground">
                {metadata.dataType.join(", ")}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Hash className="size-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Feature Count</div>
              <div className="text-muted-foreground">
                {metadata.totalFeatureCount.toLocaleString()}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <RefreshCw className="size-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Update Frequency</div>
              <div className="text-muted-foreground">
                {metadata.updateFrequency}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Shield className="size-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Licence</div>
              <div className="text-muted-foreground">
                {metadata.license}
                {metadata.licenseLink && (
                  <a
                    href={metadata.licenseLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-1 inline-flex align-middle text-primary hover:underline"
                  >
                    <ExternalLink className="size-3" />
                  </a>
                )}
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <div className="font-medium mb-2">Formats</div>
            <div className="flex flex-wrap gap-1">
              {definitionsMetadata.formats.map((format) => (
                <Badge key={format} variant="secondary" className="text-xs">
                  {format}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <div className="font-medium mb-2">Datums</div>
            <div className="flex flex-wrap gap-1">
              {definitionsMetadata.datums.map((datum) => (
                <Badge
                  key={datum}
                  variant="outline"
                  className="text-xs font-mono"
                >
                  {datum}
                </Badge>
              ))}
            </div>
          </div>

          <Separator />

          <div>
            <div className="font-medium mb-2">Links</div>
            <div className="space-y-2">
              <RouterLink
                target="_blank"
                to={{ pathname: latestReleaseReportLink }}
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="size-3" />
                Latest Release Report
              </RouterLink>
              <RouterLink
                target="_blank"
                to={{ pathname: productDescriptionLink }}
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="size-3" />
                Product Description
              </RouterLink>
              <button
                onClick={() => show()}
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="size-3" />
                Support
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
